<template>
  <div class="animated fadeIn">
    <b-row class="justify-content-center">
      <b-col md="8" sm="12">
        <b-card no-body class="mx-6">
          <b-card-header>
            <b-link :variant="''" v-b-toggle.criteria>
              <h4>Transaction Selection Criteria</h4>
            </b-link>
          </b-card-header>
          <b-collapse visible id="criteria" class="mt-2">
            <b-card-body class="p-6">
              <b-form>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <label for="name">Date Range</label>
                      <date-picker
                        v-model="selectedRange"
                        class="form-control"
                        type="date"
                        :not-after="new Date()"
                        lang="en"
                        range
                        :shortcuts="rangeShortcut"
                        format="YYYY-MM-DD"
                        range-separator="thru"
                        confirm
                      ></date-picker>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <label for="name">Prescriber NPI</label>
                      <b-form-input
                        v-model="prescriberNPI"
                        type="text"
                        placeholder="Enter a Prescriber NPI code e.g. 1234567890"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <label for="name">Prescriber State</label>
                      <b-form-select v-model="prescriberState" :options="usStates" class="mb-3" />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <label for="name">Pharmacy NPI</label>
                      <b-form-input
                        v-model="pharmacyNPI"
                        type="text"
                        placeholder="Enter a Pharmacy NPI code e.g. 1234567890"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col sm="12">
                    <b-form-group>
                      <label for="name">Pharma Company</label>
                      <b-form-input
                        v-model="pharmaCompany"
                        type="text"
                        placeholder="Enter Pharma Company name"
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <b-form-group>
                      <b-form-checkbox-group
                        id="selectedStatuses"
                        name="selectedStatuses"
                        v-model="selectedStatuses"
                        :options="statuses"
                      ></b-form-checkbox-group>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="3" sm="3">
                    <b-button variant="light" block v-on:click="clear()">Clear</b-button>
                  </b-col>
                  <b-col cols="3" sm="3" offset-sm="3">
                    <b-button variant="primary" block v-on:click="newSearch()">
                      Search
                      <i v-if="isSearchBusy" class="fa fa-spinner fa-pulse button-spinner"></i>
                    </b-button>
                  </b-col>
                  <b-col cols="3" sm="3">
                    <b-button variant="primary" block v-b-modal.exportFieldChooser>
                      Export
                      <i v-if="isExportBusy" class="fa fa-spinner fa-pulse button-spinner"></i>
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>
    <b-row v-if="searchResults">
      <b-col lg="12">
        <b-card>
          <b-card-header>
            <b-row>
              <b-col>
                <h4>Transactions</h4>
              </b-col>
              <b-col>
                <div class="pull-right">
                  <h5>Total Records: {{searchResults.total}}</h5>
                </div>
              </b-col>
            </b-row>
          </b-card-header>
          <b-table
            :sort:by.sync="sortBy"
            @sort-changed="sortingChanged"
            :sort-desc.sync="sortDesc"
            striped
            hover
            :fields="fields"
            :items="searchResults.records"
            no-local-sorting
          >
            <template slot="message_id" slot-scope="data">
              <b-button
                class="substatus-button"
                :variant="'link'"
                @click.stop="data.toggleDetails"
              >{{data.item.message_id}}</b-button>
            </template>
            <template slot="sub_status_text" slot-scope="data">
              <span v-if="data.additional_information">
                <b-popover
                  :show.sync="show"
                  :placement="'bottom'"
                  v-bind:target="data.item.message_id"
                  title="Addition Information"
                >{{data.item.additional_information}}</b-popover>
                <b-btn v-bind:id="data.item.message_id" variant="link">{{data.item.sub_status_text}}</b-btn>
              </span>
              <span v-if="!data.item.additional_information">{{data.item.sub_status_text}}</span>
            </template>
            <template slot="row-details" slot-scope="row">
              <b-card>
                <span v-if="row.item.detail_messages">
                  <VPARowDetail v-bind:details="row.item.detail_messages"></VPARowDetail>
                </span>
                <b-button :variant="'link'" class="float-right" @click.stop="row.toggleDetails">Hide</b-button>
              </b-card>
            </template>
          </b-table>
          <b-pagination
            align="center"
            :total-rows="searchResults.total"
            v-model="currentPage"
            :per-page="size"
            @input="changePage"
          ></b-pagination>
        </b-card>
      </b-col>
    </b-row>
    <ExportFieldChooser :fields="this.exportFields" v-on:saveExportFields="saveExportFields" v-on:doExport="exportData"></ExportFieldChooser>
  </div>
</template>

<script>
  import DatePicker from "vue2-datepicker";
  import { PA_HISTORY_SEARCH, PA_HISTORY_EXPORT } from "@/store/pa/actions.type";
  import { SAVE_PA_EXPORT_FIELDS } from "@/store/pa/mutations.type";
  import VPARowDetail from "@/components/VPARowDetail";
  import { mapState } from "vuex";
  import ExportFieldChooser from "../components/ExportFieldChooser";
  import * as _ from "lodash";

  export default {
    name: "PADashboard",
    components: {
      DatePicker,
      VPARowDetail,
      ExportFieldChooser
    },
    data() {
      return {
        currentPage: 1,
        size: 20,
        usStates: usStates,
        prescriberNPI: "",
        prescriberState: "",
        pharmacyNPI: "",
        pharmaCompany: "",
        selectedRange: [getStartDate(), getEndDate()],
        selectedStatuses: [
          "Sent",
          "NotSent",
          "DeliveryFailure",
          "ResponseReceived",
          "ResponseFailure",
          "PAApproved",
          "PADenied"
        ],
        statuses: [
          { text: "Not Sent", value: "NotSent", type: "status" },
          { text: "Sent", value: "Sent", type: "status" },
          { text: "Delivery Failure", value: "DeliveryFailure", type: "status" },
          { text: "Response Received", value: "ResponseReceived", type: "status" },
          { text: "Response Failure", value: "ResponseFailure", type: "status" },
          { text: "PA Approved", value: "PAApproved", type: "substatus" },
          { text: "PA Denied", value: "PADenied", type: "substatus" }
        ],
        rangeShortcut: [
          {
            text: "previous 7 days",
            onClick: () => {
              let d = new Date();
              d.setDate(d.getDate() - 7);
              d.setHours(0, 0, 0, 0);
              this.selectedRange = [d, getEndDate()];
            }
          },
          {
            text: "previous 30 days",
            onClick: () => {
              let d = new Date();
              d.setDate(d.getDate() - 30);
              d.setHours(0, 0, 0, 0);
              this.selectedRange = [d, getEndDate()];
            }
          }
        ],
        sortBy: "request_date_time",
        sortDesc: true,
        fields: [
          {
            key: "message_id",
            label: "Transaction ID",
            sortable: true
          },
          {
            key: "claim_id",
            label: "Claim ID",
            sortable: true
          },
          {
            key: "source",
            label: "Source",
            sortable: true
          },
          {
            key: "request_date_time",
            label: "Sent Date",
            sortable: true,
            formatter: value => {
              return new Date(value).toLocaleString();
            }
          },
          {
            key: "date_filled",
            label: "Claim Date",
            sortable: true,
            formatter: value => {
              let d = new Date(new Date(value).toLocaleString());
              let month = "" + (d.getMonth() + 1);
              let day = "" + d.getDate();
              let year = d.getFullYear();

              if (month.length < 2) month = "0" + month;
              if (day.length < 2) day = "0" + day;

              return [year, month, day].join("-");
            }
          },
          { key: "ndc_num", label: "NDC", sortable: true },
          { key: "drug_name", label: "Drug Description", sortable: true },
          {
            key: "status_text",
            label: "Status",
            sortable: true,
            formatter: value => {
              var status = _.find(this.statuses, { value: value });
              if (status) {
                return status.text;
              }
              return value;
            }
          },
          { key: "sub_status_text", label: "Status Description", sortable: true },
          { key: "manufacture_name", label: "Pharma", sortable: true },
          { key: "prescriber_npi_num", label: "Physician NPI", sortable: true },
          { key: "pharmacy_npi_num", label: "Pharmacy NPI", sortable: true }
        ],
        exportFields: [],
        defaultExportFields: [
          { key: 'additional_information', name: 'Additional Information', active: false, selected: false },
          { key: 'claim_id', name: 'Claim ID', active: false, selected: true },
          { key: 'cob_primary_reject_code1', name: 'Cob Primary Reject Code1', active: false, selected: false },
          { key: 'cob_primary_reject_code2', name: 'Cob Primary Reject Code2', active: false, selected: false },
          { key: 'cob_primary_reject_code3', name: 'Cob Primary Reject Code3', active: false, selected: false },
          { key: 'cob_secondary_reject_code1', name: 'Cob Secondary Reject Code1', active: false, selected: false },
          { key: 'cob_secondary_reject_code2', name: 'Cob Secondary Reject Code2', active: false, selected: false },
          { key: 'cob_secondary_reject_code3', name: 'Cob Secondary Reject Code3', active: false, selected: false },
          { key: 'cob_tertiary_reject_code1', name: 'Cob Tertiary Reject Code1', active: false, selected: false },
          { key: 'cob_tertiary_reject_code2', name: 'Cob Tertiary Reject Code2', active: false, selected: false },
          { key: 'cob_tertiary_reject_code3', name: 'Cob Tertiary Reject Code3', active: false, selected: false },
          { key: 'date_filled', name: 'Date Filled', active: false, selected: true },
          { key: 'days_supply', name: 'Days Supply', active: false, selected: false },
          { key: 'denial_reason', name: 'Denial Reason', active: false, selected: false },
          { key: 'denial_reason_code', name: 'Denial Reason Code', active: false, selected: false },
          { key: 'drug_name', name: 'Drug Name', active: false, selected: true },
          { key: 'file_name', name: 'File Name', active: false, selected: false },
          { key: 'is_retry', name: 'Is Retry', active: false, selected: false },
          { key: 'manufacture_name', name: 'Manufacturer Name', active: false, selected: true },
          { key: 'message_id', name: 'Message ID', active: false, selected: true },
          { key: 'ndc_num', name: 'NDC', active: false, selected: true },
          { key: 'new_or_refill', name: 'New Or Refill', active: false, selected: false },
          { key: 'original_message_id', name: 'Original Message ID', active: false, selected: false },
          { key: 'pharmacy_name', name: 'Pharmacy Name', active: false, selected: false },
          { key: 'pharmacy_ncpdp_number', name: 'Pharmacy NCPDP', active: false, selected: false },
          { key: 'pharmacy_npi_num', name: 'Pharmacy NPI', active: false, selected: true },
          { key: 'prescriber_dea', name: 'Prescriber DEA', active: false, selected: false },
          { key: 'prescriber_fn', name: 'Prescriber First Name', active: false, selected: false },
          { key: 'prescriber_ln', name: 'Prescriber Last Name', active: false, selected: false },
          { key: 'prescriber_npi_num', name: 'Prescriber NPI', active: false, selected: true },
          { key: 'prescriber_state', name: 'Prescriber State', active: false, selected: false },
          { key: 'primary_payer_bin', name: 'Primary Payer Bin', active: false, selected: false },
          { key: 'primary_payer_group_name', name: 'Primary Payer Group Name', active: false, selected: false },
          { key: 'primary_payer_pcn', name: 'Primary Payer Pcn', active: false, selected: false },
          { key: 'primary_payer_plan_name', name: 'Primary Payer Plan Name', active: false, selected: false },
          { key: 'quantity_dispensed', name: 'Quantity Dispensed', active: false, selected: false },
          { key: 'record_create_date', name: 'Record Create Date', active: false, selected: false },
          { key: 'reference_number', name: 'Reference Number', active: false, selected: false },
          { key: 'request_date_time', name: 'Request Date', active: false, selected: true },
          { key: 'retry_attempt_count', name: 'Retry Attempt Count', active: false, selected: false },
          { key: 'rx_number_text', name: 'Rx Number', active: false, selected: false },
          { key: 'send_errors_text', name: 'Send Errors', active: false, selected: false },
          { key: 'source', name: 'Source', active: false, selected: true },
          { key: 'status_text', name: 'Status', active: false, selected: true },
          { key: 'sub_status_text', name: 'SubStatus', active: false, selected: true }
        ]
      };
    },
    created: function() {
      if (this.exportFields.length === 0) {
        if (this.savedExportFields && this.savedExportFields.length > 0) {
          this.exportFields = Object.assign([], this.savedExportFields);
        }
        this.exportFields = Object.assign([], this.defaultExportFields);
      }
    },
    beforeMount: function() {
      if (this.savedExportFields && this.savedExportFields.length > 0) {
          this.exportFields = Object.assign([], this.savedExportFields);
        }
    },
    computed: {
      ...mapState({
        searchCriteria: state => state.pa.searchCriteria,
        searchResults: state => state.pa.searchResults,
        isExportBusy: state => state.pa.isExportBusy,
        isSearchBusy: state => state.pa.isSearchBusy,
        savedExportFields: state => state.pa.savedExportFields
      })
    },
    methods: {
      newSearch() {
        if (!this.$store.state.pa.isSearchBusy) {
          this.currentPage = 1;
          this.search();
        }
      },
      search() {
        this.$root.$emit("bv::hide::popover");
        if (!this.$store.state.pa.isSearchBusy) {
          this.$store.dispatch(PA_HISTORY_SEARCH, this.getCriteria());
        }
      },
      clear() {
        this.prescriberNPI = "";
        this.prescriberState = "";
        this.pharmacyNPI = "";
        this.pharmaCompany = "";
        this.selectedStatuses = [
          "Sent",
          "NotSent",
          "DeliveryFailure",
          "ResponseReceived",
          "ResponseFailure",
          "PAApproved",
          "PADenied"
        ];
        this.selectedRange = [getStartDate(), getEndDate()];
      },
      exportData(selectedFields) {
        if (!this.$store.state.pa.isExportBusy) {
          var criteria = this.getCriteria();
          criteria.size = 10000;
          criteria.page = 1;
          var mergedCriteria = Object.assign(criteria, selectedFields);
          this.$store.dispatch(PA_HISTORY_EXPORT, mergedCriteria);
        }
      },
      saveExportFields(fields) {
        this.$store.commit(SAVE_PA_EXPORT_FIELDS, fields);
      },
      changePage(page) {
        this.search();
      },
      getCriteria() {
        let statusItems = _.filter(this.statuses, { type: "status" });
        let substatusItems = _.filter(this.statuses, { type: "substatus" });
        this.selectedRange[0].setHours(0, 0, 0, 0);
        this.selectedRange[1].setHours(23, 59, 59, 999);

        let criteria = {
          StartDate: new Date(this.selectedRange[0].toUTCString()).toISOString(),
          EndDate: new Date(this.selectedRange[1].toUTCString()).toISOString(),
          PrescriberNPI: this.prescriberNPI,
          PrescriberState: this.prescriberState,
          PharmacyNPI: this.pharmacyNPI,
          PharmaCompany: this.pharmaCompany,
          Statuses: _.filter(
            this.selectedStatuses,
            selectedStatus =>
              _.findIndex(statusItems, { value: selectedStatus }) >= 0
          ),
          SubStatuses: _.filter(
            this.selectedStatuses,
            selectedStatus =>
              _.findIndex(substatusItems, { value: selectedStatus }) >= 0
          ),
          Page: this.currentPage,
          Size: this.size,
          SortDesc: this.sortDesc,
          SortBy: this.sortBy.charAt(0).toLowerCase() + this.sortBy.slice(1)
        };
        return criteria;
      },
      sortingChanged(ctx) {
        this.sortDesc = ctx.sortDesc;
        this.sortBy = ctx.sortBy;
        this.search();
      }
    }
  };

  function getStartDate() {
    let startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0);
    return startDate;
  }

  function getEndDate() {
    let endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  }

  const usStates = {
    AL: "Alabama",
    AK: "Alaska",
    AS: "American Samoa",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    DC: "District Of Columbia",
    FM: "Federated States Of Micronesia",
    FL: "Florida",
    GA: "Georgia",
    GU: "Guam",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MH: "Marshall Islands",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    MP: "Northern Mariana Islands",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PW: "Palau",
    PA: "Pennsylvania",
    PR: "Puerto Rico",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VI: "Virgin Islands",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming"
  };
</script>

<style>
  .substatus-button {
    padding: 0px;
  }
</style>



// WEBPACK FOOTER //
// src/views/PAPortal.vue